import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import jwtService from '../services/jwtService';

interface CronStatus {
  isRunning: boolean;
  lastRun: string | null;
  nextRun: string | null;
  runCount: number;
  errorCount: number;
  uptime: number;
  tokenRefresh: {
    isRunning: boolean;
    lastRun: string | null;
    nextRun: string | null;
    runCount: number;
    errorCount: number;
    uptime: number;
  };
}

interface RateStatistics {
  total: number;
  active: number;
  inactive: number;
  expired: number;
  expiringSoon: number;
  uniqueBlockIds: number;
  hasCurrentRates: boolean;
}

interface RecentRate {
  id: string;
  gold_buy_rate: number;
  gold_sell_rate: number;
  silver_buy_rate: number;
  silver_sell_rate: number;
  block_id: string;
  expires_at: string;
  is_active: boolean;
  created_at: string;
  source: string;
}

interface CronData {
  cron: CronStatus;
  statistics: RateStatistics | null;
  recentRates: RecentRate[];
  currentTime: string;
}

const CronManagement: React.FC = () => {
  const [cronData, setCronData] = useState<CronData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const API_BASE = `${process.env.REACT_APP_API_URL}/cron-management`;

  // Fetch cron status
  const fetchCronStatus = useCallback(async () => {
    try {
      setLoading(true);
      const authHeader = jwtService.getAdminAuthHeader();
      const response = await axios.get(`${API_BASE}/status`, {
        headers: authHeader ? { 'Authorization': authHeader } : {}
      });
      setCronData(response.data.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch cron status');
      console.error('Error fetching cron status:', err);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    fetchCronStatus();
    const interval = setInterval(fetchCronStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchCronStatus]);

  // Handle cron actions
  const handleCronAction = async (action: string) => {
    try {
      setActionLoading(action);
      const authHeader = jwtService.getAdminAuthHeader();
      const response = await axios.post(`${API_BASE}/${action}`, {}, {
        headers: authHeader ? { 'Authorization': authHeader } : {}
      });
      
      if (response.data.success) {
        // Refresh status after action
        await fetchCronStatus();
      } else {
        setError(`Failed to ${action}: ${response.data.error}`);
      }
    } catch (err) {
      setError(`Failed to ${action}`);
      console.error(`Error in ${action}:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  // formatDuration removed as it was unused and triggered linter error

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white/90 backdrop-blur-sm p-6" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white/90 backdrop-blur-sm p-6" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Cron Management Dashboard
          </h1>
          <p className="text-white">
            Monitor and control automated rate fetching and token refresh
          </p>
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="text-red-400">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {cronData && (
          <>
            {/* Control Panel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Rate Fetching Cron */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Rate Fetching Cron
                  </h3>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    cronData.cron.isRunning 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {cronData.cron.isRunning ? 'Running' : 'Stopped'}
                  </div>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Last Run:</span>
                    <span className="text-sm font-medium">
                      {formatDate(cronData.cron.lastRun)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Next Run:</span>
                    <span className="text-sm font-medium">
                      {formatDate(cronData.cron.nextRun)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Run Count:</span>
                    <span className="text-sm font-medium">{cronData.cron.runCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Error Count:</span>
                    <span className="text-sm font-medium text-red-600">
                      {cronData.cron.errorCount}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <button
                    onClick={() => handleCronAction('start-rates')}
                    disabled={cronData.cron.isRunning || actionLoading === 'start-rates'}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'start-rates' ? 'Starting...' : 'Start'}
                  </button>
                  <button
                    onClick={() => handleCronAction('stop-rates')}
                    disabled={!cronData.cron.isRunning || actionLoading === 'stop-rates'}
                    className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'stop-rates' ? 'Stopping...' : 'Stop'}
                  </button>
                  <button
                    onClick={() => handleCronAction('manual-fetch-rates')}
                    disabled={actionLoading === 'manual-fetch-rates'}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'manual-fetch-rates' ? 'Fetching...' : 'Manual Fetch'}
                  </button>
                </div>
              </div>

              {/* Token Refresh Cron */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Token Refresh Cron
                  </h3>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    cronData.cron.tokenRefresh.isRunning 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {cronData.cron.tokenRefresh.isRunning ? 'Running' : 'Stopped'}
                  </div>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Last Run:</span>
                    <span className="text-sm font-medium">
                      {formatDate(cronData.cron.tokenRefresh.lastRun)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Next Run:</span>
                    <span className="text-sm font-medium">
                      {formatDate(cronData.cron.tokenRefresh.nextRun)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Run Count:</span>
                    <span className="text-sm font-medium">{cronData.cron.tokenRefresh.runCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Error Count:</span>
                    <span className="text-sm font-medium text-red-600">
                      {cronData.cron.tokenRefresh.errorCount}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <button
                    onClick={() => handleCronAction('start-token')}
                    disabled={cronData.cron.tokenRefresh.isRunning || actionLoading === 'start-token'}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'start-token' ? 'Starting...' : 'Start'}
                  </button>
                  <button
                    onClick={() => handleCronAction('stop-token')}
                    disabled={!cronData.cron.tokenRefresh.isRunning || actionLoading === 'stop-token'}
                    className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'stop-token' ? 'Stopping...' : 'Stop'}
                  </button>
                  <button
                    onClick={() => handleCronAction('manual-refresh-token')}
                    disabled={actionLoading === 'manual-refresh-token'}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'manual-refresh-token' ? 'Refreshing...' : 'Manual Refresh'}
                  </button>
                </div>
              </div>

              {/* Statistics */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Rate Statistics
                </h3>
                
                {cronData.statistics ? (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Rates:</span>
                      <span className="text-sm font-medium">{cronData.statistics.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Active Rates:</span>
                      <span className="text-sm font-medium text-green-600">
                        {cronData.statistics.active}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Inactive Rates:</span>
                      <span className="text-sm font-medium text-gray-600">
                        {cronData.statistics.inactive}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Expired Rates:</span>
                      <span className="text-sm font-medium text-red-600">
                        {cronData.statistics.expired}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Expiring Soon:</span>
                      <span className="text-sm font-medium text-yellow-600">
                        {cronData.statistics.expiringSoon}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Unique Block IDs:</span>
                      <span className="text-sm font-medium">{cronData.statistics.uniqueBlockIds}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No statistics available</p>
                )}

                <div className="mt-4 space-y-2">
                  <button
                    onClick={() => handleCronAction('mark-expired')}
                    disabled={actionLoading === 'mark-expired'}
                    className="w-full bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'mark-expired' ? 'Cleaning...' : 'Clean Expired Rates'}
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Rates Table */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Recent Rates ({cronData.recentRates.length})
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white/90 backdrop-blur-sm">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Block ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gold Buy
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gold Sell
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Silver Buy
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Silver Sell
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expires At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cronData.recentRates.map((rate) => (
                      <tr key={rate.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {rate.block_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{rate.gold_buy_rate.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{rate.gold_sell_rate.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{rate.silver_buy_rate.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{rate.silver_sell_rate.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            rate.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {rate.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(rate.expires_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(rate.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CronManagement; 